import * as React from 'react';
import { PencilIcon, CheckIcon, XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { apiUrl, authFetch, authService } from '../utils/api';

interface ContactInfo {
  id?: number;
  whatsappNumber: string;
  facebookPage: string;
  youtubePage: string;
  tiktokChannel: string;
}

const ContactUs: React.FC = () => {
  const [contactInfo, setContactInfo] = React.useState<ContactInfo>({
    whatsappNumber: '',
    facebookPage: '',
    youtubePage: '',
    tiktokChannel: '',
  });

  const [isEditing, setIsEditing] = React.useState(false);
  const [editedInfo, setEditedInfo] = React.useState<ContactInfo>({
    whatsappNumber: '',
    facebookPage: '',
    youtubePage: '',
    tiktokChannel: '',
  });
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [saveMessage, setSaveMessage] = React.useState<{type: 'success' | 'error', text: string} | null>(null);

  // Fetch contact info from API on component mount
  React.useEffect(() => {
    const fetchContactInfo = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await authFetch(apiUrl('Contact/getAll?pageNumber=1&pageSize=10'), {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });
        
        const data = await response.json();
        
        if (data.success && data.data && data.data.length > 0) {
          // The API returns an array of contacts, we'll use the first one
          const contactData = data.data[0];
          setContactInfo({
            id: contactData.id,
            whatsappNumber: contactData.whatsApp_Number || '',
            facebookPage: contactData.facebook_Page || '',
            youtubePage: contactData.youTube_Channel || '',
            tiktokChannel: contactData.tiktokChannel || '',
          });
        } else {
          // If no contact info exists yet, leave the state as empty strings
          console.log('No contact information found or error fetching it');
        }
      } catch (error) {
        console.error('Error fetching contact information:', error);
        setError('Failed to load contact information. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchContactInfo();
  }, []);

  // Update editedInfo when contactInfo changes or editing starts
  React.useEffect(() => {
    if (isEditing) {
      setEditedInfo({ ...contactInfo });
    }
  }, [isEditing, contactInfo]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditedInfo(prev => ({ ...prev, [name]: value }));
  };

  const startEditing = () => {
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setSaveMessage(null);
  };

  const saveChanges = async () => {
    setIsSaving(true);
    setSaveMessage(null);
    
    try {
      if (!contactInfo.id) {
        throw new Error('Contact ID not found');
      }

      const response = await authFetch(apiUrl(`Contact/update/${contactInfo.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          whatsApp_Number: editedInfo.whatsappNumber,
          facebook_Page: editedInfo.facebookPage,
          youTubeChannel: editedInfo.youtubePage,
          tiktokChannel: editedInfo.tiktokChannel
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setContactInfo({
          id: data.data.id,
          whatsappNumber: data.data.whatsApp_Number || '',
          facebookPage: data.data.facebook_Page || '',
          youtubePage: data.data.youTubeChannel || '',
          tiktokChannel: data.data.tiktokChannel || '',
        });
        setSaveMessage({
          type: 'success',
          text: 'Contact information updated successfully'
        });
        setIsEditing(false);
      } else {
        setSaveMessage({
          type: 'error',
          text: data.message || 'Failed to update contact information'
        });
      }
    } catch (error) {
      console.error('Error saving contact information:', error);
      setSaveMessage({
        type: 'error',
        text: error instanceof Error && error.message === 'Contact ID not found' 
          ? 'Cannot update contact: no existing contact found' 
          : 'Failed to update contact information. Please try again later.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const validateUrl = (url: string) => {
    if (!url) return true; // Empty URLs are allowed
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };

  const validateWhatsApp = (number: string) => {
    if (!number) return true; // Empty numbers are allowed
    return /^\+?[0-9\s-()]+$/.test(number);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Contact Information</h1>
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {saveMessage && (
        <div className={`mb-6 p-4 rounded-lg ${
          saveMessage.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          <div className="flex">
            <div className="flex-shrink-0">
              {saveMessage.type === 'success' ? (
                <CheckIcon className="h-5 w-5 text-green-400" aria-hidden="true" />
              ) : (
                <ExclamationTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
              )}
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{saveMessage.text}</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow-md p-6">
        {isEditing ? (
          <>
            <div className="space-y-6">
              <div>
                <label htmlFor="whatsappNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  WhatsApp Number
                </label>
                <input
                  type="text"
                  id="whatsappNumber"
                  name="whatsappNumber"
                  value={editedInfo.whatsappNumber}
                  onChange={handleInputChange}
                  placeholder="e.g., +1 234 567 8901"
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring focus:ring-opacity-50 ${!validateWhatsApp(editedInfo.whatsappNumber) ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary focus:ring-primary'}`}
                />
                {!validateWhatsApp(editedInfo.whatsappNumber) && (
                  <p className="mt-1 text-sm text-red-600">Please enter a valid phone number</p>
                )}
              </div>
              
              <div>
                <label htmlFor="facebookPage" className="block text-sm font-medium text-gray-700 mb-1">
                  Facebook Page URL
                </label>
                <input
                  type="text"
                  id="facebookPage"
                  name="facebookPage"
                  value={editedInfo.facebookPage}
                  onChange={handleInputChange}
                  placeholder="e.g., https://facebook.com/yourpage"
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring focus:ring-opacity-50 ${!validateUrl(editedInfo.facebookPage) ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary focus:ring-primary'}`}
                />
                {!validateUrl(editedInfo.facebookPage) && (
                  <p className="mt-1 text-sm text-red-600">Please enter a valid URL</p>
                )}
              </div>
              
              <div>
                <label htmlFor="youtubePage" className="block text-sm font-medium text-gray-700 mb-1">
                  YouTube Channel URL
                </label>
                <input
                  type="text"
                  id="youtubePage"
                  name="youtubePage"
                  value={editedInfo.youtubePage}
                  onChange={handleInputChange}
                  placeholder="e.g., https://youtube.com/c/yourchannel"
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring focus:ring-opacity-50 ${!validateUrl(editedInfo.youtubePage) ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary focus:ring-primary'}`}
                />
                {!validateUrl(editedInfo.youtubePage) && (
                  <p className="mt-1 text-sm text-red-600">Please enter a valid URL</p>
                )}
              </div>

              <div>
                <label htmlFor="tiktokChannel" className="block text-sm font-medium text-gray-700 mb-1">
                  TikTok Channel URL
                </label>
                <input
                  type="text"
                  id="tiktokChannel"
                  name="tiktokChannel"
                  value={editedInfo.tiktokChannel}
                  onChange={handleInputChange}
                  placeholder="e.g., https://tiktok.com/@username"
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring focus:ring-opacity-50 ${!validateUrl(editedInfo.tiktokChannel) ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary focus:ring-primary'}`}
                />
                {!validateUrl(editedInfo.tiktokChannel) && (
                  <p className="mt-1 text-sm text-red-600">Please enter a valid URL</p>
                )}
              </div>
            </div>
            
            <div className="mt-6 flex space-x-3">
              <button
                onClick={saveChanges}
                disabled={
                  isSaving || 
                  !validateWhatsApp(editedInfo.whatsappNumber) || 
                  !validateUrl(editedInfo.facebookPage) || 
                  !validateUrl(editedInfo.youtubePage) ||
                  !validateUrl(editedInfo.tiktokChannel)
                }
                className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckIcon className="h-5 w-5 mr-2" />
                    Save
                  </>
                )}
              </button>
              
              <button
                onClick={cancelEditing}
                className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                disabled={isSaving}
              >
                <XMarkIcon className="h-5 w-5 mr-2" />
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500">WhatsApp Number</h3>
                <p className="mt-1 text-lg">
                  {contactInfo.whatsappNumber || (
                    <span className="text-gray-400 italic">Not set</span>
                  )}
                </p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">Facebook Page</h3>
                {contactInfo.facebookPage ? (
                  <a 
                    href={contactInfo.facebookPage} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="mt-1 text-lg text-blue-600 hover:underline block"
                  >
                    {contactInfo.facebookPage}
                  </a>
                ) : (
                  <p className="mt-1 text-lg text-gray-400 italic">Not set</p>
                )}
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">YouTube Channel</h3>
                {contactInfo.youtubePage ? (
                  <a 
                    href={contactInfo.youtubePage} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="mt-1 text-lg text-blue-600 hover:underline block"
                  >
                    {contactInfo.youtubePage}
                  </a>
                ) : (
                  <p className="mt-1 text-lg text-gray-400 italic">Not set</p>
                )}
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500">TikTok Channel</h3>
                {contactInfo.tiktokChannel ? (
                  <a 
                    href={contactInfo.tiktokChannel} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="mt-1 text-lg text-blue-600 hover:underline block"
                  >
                    {contactInfo.tiktokChannel}
                  </a>
                ) : (
                  <p className="mt-1 text-lg text-gray-400 italic">Not set</p>
                )}
              </div>
            </div>
            
            <button
              onClick={startEditing}
              className="mt-6 flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              <PencilIcon className="h-5 w-5 mr-2" />
              Edit Contact Information
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ContactUs;
